/**
 * The one source of truth for the onboarding step order.
 *
 * A new step is added here and nowhere else: this file owns the ordered list
 * of onboarding steps, the total shown in `Step N of 6`, the mapping between
 * a server `onboardingStep` value and a URL step slug, and which steps this
 * app can currently draw.
 */

export type typeOnboardingServerStep =
  | "username"
  | "details"
  | "signInMethods"
  | "photo"
  | "sports"
  | "leagues"
  | "done";

export type typeOnboardingStepSlug =
  "username" | "about-you" | "sign-in-methods" | "photo" | "sports" | "leagues";

export type typeOnboardingStepEntry = {
  /** The value profile-api stores in `onboardingStep`. */
  serverStep: Exclude<typeOnboardingServerStep, "done">;
  /** The value that goes in the `?step=` query param. */
  slug: typeOnboardingStepSlug;
  /** 1-based, used by the `Step N of 6` line and the progress fill. */
  index: number;
  /** False until the issue that builds this step ships. */
  isImplemented: boolean;
};

export const ONBOARDING_STEPS: readonly typeOnboardingStepEntry[] = [
  { serverStep: "username", slug: "username", index: 1, isImplemented: true },
  { serverStep: "details", slug: "about-you", index: 2, isImplemented: true },
  {
    serverStep: "signInMethods",
    slug: "sign-in-methods",
    index: 3,
    isImplemented: true,
  },
  { serverStep: "photo", slug: "photo", index: 4, isImplemented: true },
  { serverStep: "sports", slug: "sports", index: 5, isImplemented: true },
  { serverStep: "leagues", slug: "leagues", index: 6, isImplemented: true },
] as const;

// The MAXIMUM a fan can see, when the sign-in-methods step is offered. The
// count a given fan actually sees comes from resolveOnboardingStepTotal
// below, because Q274 makes the sign-in-methods step optional per fan: a fan
// with nothing left to add never has it rendered and sees one step fewer.
export const TOTAL_ONBOARDING_STEPS = ONBOARDING_STEPS.length;

export const ONBOARDING_PATH = "/onboarding";

export const DEFAULT_ONBOARDING_SLUG: typeOnboardingStepSlug = "username";

export function findStepBySlug(slug: string): typeOnboardingStepEntry | null {
  return ONBOARDING_STEPS.find((entry) => entry.slug === slug) ?? null;
}

export function findStepByServerStep(
  serverStep: string,
): typeOnboardingStepEntry | null {
  return (
    ONBOARDING_STEPS.find((entry) => entry.serverStep === serverStep) ?? null
  );
}

/**
 * The steps THIS fan walks. Q274: when there is nothing left to offer, the
 * sign-in-methods step is not rendered at all and the fan's flow is one step
 * shorter, so every indexes-and-total pair below is recomputed rather than
 * read off ONBOARDING_STEPS.
 */
export function visibleOnboardingSteps(
  hasSignInMethodsStep: boolean,
): readonly typeOnboardingStepEntry[] {
  if (hasSignInMethodsStep) return ONBOARDING_STEPS;
  return ONBOARDING_STEPS.filter(
    (entry) => entry.serverStep !== "signInMethods",
  ).map((entry, position) => ({ ...entry, index: position + 1 }));
}

export function resolveOnboardingStepTotal(
  hasSignInMethodsStep: boolean,
): number {
  return visibleOnboardingSteps(hasSignInMethodsStep).length;
}

/** 1-based position of `slug` in this fan's flow, or null when it is not in it. */
export function resolveVisibleStepIndex(input: {
  slug: typeOnboardingStepSlug;
  hasSignInMethodsStep: boolean;
}): number | null {
  const entry = visibleOnboardingSteps(input.hasSignInMethodsStep).find(
    (candidate) => candidate.slug === input.slug,
  );
  return entry?.index ?? null;
}

/** Rounded whole percent for the progress bar. Step 1 of 6 is 17, step 2 of 6 is 33. */
export function stepFillPercent(index: number, total: number): number {
  return Math.round((index / total) * 100);
}

/**
 * The single source of truth for "does this fan belong in onboarding, and
 * where". `profile` is `null` when /profile/get returned `data.profile ===
 * null`. Returns the slug to send them to, or null to let them into the app.
 *
 * `hasSignInMethodsStep` defaults to true so every existing caller keeps
 * resolving against the full six-step flow; the route passes the per-fan
 * value once it knows whether this fan has anything to offer (WI-4, WI-6).
 */
export function resolveOnboardingSlug(
  input: {
    profile: { onboardingStep: string } | null;
  },
  hasSignInMethodsStep = true,
): typeOnboardingStepSlug | null {
  const { profile } = input;
  if (profile === null) return DEFAULT_ONBOARDING_SLUG;
  if (profile.onboardingStep === "done") return null;

  const entry = findStepByServerStep(profile.onboardingStep);
  // A step this app cannot draw yet lets the fan into the app rather than
  // trapping them on a blank screen.
  if (!entry?.isImplemented) return null;

  const visible = visibleOnboardingSteps(hasSignInMethodsStep);
  const directMatch = visible.find(
    (candidate) => candidate.serverStep === entry.serverStep,
  );
  if (directMatch) return directMatch.slug;

  // The requested step exists but is not in this fan's visible flow (the
  // sign-in-methods step with nothing left to offer). Walk forward to the
  // next step this fan does have, rather than stranding them on a step that
  // will never render or dumping them out of onboarding entirely.
  const requestedRank = serverStepRank(entry.serverStep);
  const forward = visible.find(
    (candidate) => serverStepRank(candidate.serverStep) >= requestedRank,
  );
  return forward?.slug ?? null;
}

const SERVER_STEP_ORDER: readonly string[] = [
  "username",
  "details",
  "signInMethods",
  "photo",
  "sports",
  "leagues",
  "done",
];

export function serverStepRank(serverStep: string | null): number {
  if (serverStep === null) return 0;
  const position = SERVER_STEP_ORDER.indexOf(serverStep);
  return position === -1 ? 0 : position + 1;
}

export function furthestServerStep(
  a: string | null,
  b: string | null,
): string | null {
  return serverStepRank(a) >= serverStepRank(b) ? a : b;
}

export type typeOnboardingPlacement =
  | { kind: "leave" }
  | { kind: "redirect"; slug: typeOnboardingStepSlug }
  | { kind: "stay"; slug: typeOnboardingStepSlug };

/**
 * The server's `onboardingStep` names the furthest step the fan may view,
 * not the step they must be on: it is a ceiling, not a destination. A
 * requested step at or behind that ceiling renders as asked; a step ahead of
 * it, or a missing step, is replaced with the ceiling. `reachedServerStep` is
 * the value a claim or update response already returned, so an advance never
 * has to wait on a cache re-read that has not caught up with the mutation
 * yet.
 *
 * `hasSignInMethodsStep` defaults to true. The route passes the per-fan
 * value (WI-4, WI-6) so a requested or ceiling slug is looked up against the
 * steps this fan actually has, not the maximum six.
 */
export function resolveOnboardingPlacement(input: {
  profile: { onboardingStep: string } | null;
  reachedServerStep: string | null;
  requestedSlug: typeOnboardingStepSlug | undefined;
  hasSignInMethodsStep?: boolean;
}): typeOnboardingPlacement {
  const {
    profile,
    reachedServerStep,
    requestedSlug,
    hasSignInMethodsStep = true,
  } = input;

  const serverStep = furthestServerStep(
    profile?.onboardingStep ?? null,
    reachedServerStep,
  );
  const ceiling =
    serverStep === null
      ? DEFAULT_ONBOARDING_SLUG
      : resolveOnboardingSlug(
          { profile: { onboardingStep: serverStep } },
          hasSignInMethodsStep,
        );

  if (ceiling === null) return { kind: "leave" };
  if (requestedSlug === undefined) return { kind: "redirect", slug: ceiling };

  const visible = visibleOnboardingSteps(hasSignInMethodsStep);
  const requestedEntry =
    visible.find((entry) => entry.slug === requestedSlug) ?? null;
  const ceilingEntry = visible.find((entry) => entry.slug === ceiling) ?? null;
  if (requestedEntry === null || ceilingEntry === null) {
    return { kind: "redirect", slug: ceiling };
  }

  if (requestedEntry.index > ceilingEntry.index) {
    return { kind: "redirect", slug: ceiling };
  }

  return { kind: "stay", slug: requestedSlug };
}
